/**
 * Hook para gerenciamento de add-ons no painel admin
 * Fornece queries e mutations para operações de add-ons
 * 
 * @version 1.0
 * @date 2025-12-19
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/**
 * Interface para estatísticas de add-ons
 */
interface AddonStats {
  activeAddons: number;
  pendingInstances: number;
  addonsGrowth: number;
}

/**
 * Interface para diagnóstico de add-ons
 */
interface AddonDiagnostics {
  addons_without_stripe: number;
  orphan_instances: number;
  cancelled_with_active_instances: number;
  total_issues: number;
  checked_at: string;
}

/**
 * Interface para tenant addon com joins
 */
export interface TenantAddonWithDetails {
  id: string;
  tenant_id: string;
  addon_id: string;
  provider_id: string | null;
  quantity: number;
  status: string;
  stripe_subscription_item_id: string | null;
  stripe_invoice_id: string | null;
  activated_at: string | null;
  cancelled_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  created_at: string;
  updated_at: string;
  // Joins
  tenant?: {
    id: string;
    name: string;
  };
  plan_addons?: {
    id: string;
    name: string;
    addon_type: string;
  };
  api_providers?: {
    id: string;
    name: string;
    slug: string;
  };
}

/**
 * Interface para log de auditoria
 */
export interface AddonAuditLog {
  id: string;
  action_type: string;
  performed_by: string;
  tenant_id: string;
  addon_id: string | null;
  provider_id: string | null;
  tenant_addon_id: string | null;
  old_data: Record<string, unknown>;
  new_data: Record<string, unknown>;
  stripe_subscription_id: string | null;
  notes: string | null;
  ip_address: string | null;
  created_at: string;
  // Joins
  tenant?: {
    name: string;
  };
  plan_addons?: {
    name: string;
  };
}

/**
 * Interface para dados de criação manual de add-on
 */
export interface CreateAddonData {
  tenantId: string;
  addonId: string;
  providerId?: string | null;
  quantity: number;
  stripeSubscriptionId?: string | null;
  stripeSubscriptionItemId?: string | null;
  notes?: string;
}

/**
 * Hook principal para gerenciamento de add-ons
 */
export function useAddonManagement() {
  const queryClient = useQueryClient();

  /**
   * Query para estatísticas gerais
   */
  const statsQuery = useQuery({
    queryKey: ['addon-management-stats'],
    queryFn: async (): Promise<AddonStats> => {
      // Busca add-ons ativos
      const { count: activeAddons, error: activeError } = await supabase
        .from('tenant_addons')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active');

      if (activeError) throw activeError;

      // Busca instâncias pendentes
      const { count: pendingInstances, error: pendingError } = await supabase
        .from('provider_instances')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      if (pendingError) throw pendingError;

      return {
        activeAddons: activeAddons ?? 0,
        pendingInstances: pendingInstances ?? 0,
        addonsGrowth: 0, // TODO: calcular crescimento
      };
    },
    staleTime: 30000, // 30 segundos
  });

  /**
   * Query para diagnóstico de problemas
   */
  const diagnosticsQuery = useQuery({
    queryKey: ['addon-management-diagnostics'],
    queryFn: async (): Promise<AddonDiagnostics> => {
      const { data, error } = await supabase.rpc('fn_get_addon_diagnostics');
      
      if (error) throw error;
      
      return data as unknown as AddonDiagnostics;
    },
    staleTime: 60000, // 1 minuto
  });

  /**
   * Query para listar todos os add-ons com detalhes
   */
  const addonsQuery = useQuery({
    queryKey: ['addon-management-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenant_addons')
        .select(`
          *,
          tenant:tenants(id, name),
          plan_addons(id, name, addon_type),
          api_providers(id, name, slug)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return (data || []) as unknown as TenantAddonWithDetails[];
    },
    staleTime: 30000,
  });

  /**
   * Query para listar logs de auditoria
   */
  const auditLogsQuery = useQuery({
    queryKey: ['addon-management-audit'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('addon_audit_logs')
        .select(`
          *,
          tenant:tenants(name),
          plan_addons(name)
        `)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      return (data || []) as unknown as AddonAuditLog[];
    },
    staleTime: 30000,
  });

  /**
   * Mutation para criar add-on manualmente
   */
  const createAddonMutation = useMutation({
    mutationFn: async (data: CreateAddonData) => {
      const { data: result, error } = await supabase.rpc('fn_create_addon_manual', {
        p_tenant_id: data.tenantId,
        p_addon_id: data.addonId,
        p_provider_id: data.providerId || null,
        p_quantity: data.quantity,
        p_stripe_subscription_id: data.stripeSubscriptionId || null,
        p_stripe_subscription_item_id: data.stripeSubscriptionItemId || null,
        p_notes: data.notes || null,
      });

      if (error) throw error;

      return result;
    },
    onSuccess: (data) => {
      toast.success('Add-on criado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['addon-management'] });
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar add-on: ${error.message}`);
    },
  });

  /**
   * Mutation para cancelar add-on
   */
  const cancelAddonMutation = useMutation({
    mutationFn: async ({ tenantAddonId, notes }: { tenantAddonId: string; notes?: string }) => {
      const { data: result, error } = await supabase.rpc('fn_cancel_addon_manual', {
        p_tenant_addon_id: tenantAddonId,
        p_notes: notes || null,
      });

      if (error) throw error;

      return result;
    },
    onSuccess: () => {
      toast.success('Add-on cancelado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['addon-management'] });
    },
    onError: (error: Error) => {
      toast.error(`Erro ao cancelar add-on: ${error.message}`);
    },
  });

  /**
   * Função para sincronizar com Stripe
   */
  const syncWithStripe = async (tenantId: string, subscriptionId?: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.access_token) {
      throw new Error('Sessão não encontrada');
    }

    const response = await supabase.functions.invoke('admin-sync-addon', {
      body: {
        action: subscriptionId ? 'sync' : 'check',
        tenantId,
        subscriptionId,
      },
    });

    if (response.error) {
      throw new Error(response.error.message);
    }

    return response.data;
  };

  /**
   * Função para refetch de todos os dados
   */
  const refetchAll = () => {
    queryClient.invalidateQueries({ queryKey: ['addon-management-stats'] });
    queryClient.invalidateQueries({ queryKey: ['addon-management-diagnostics'] });
    queryClient.invalidateQueries({ queryKey: ['addon-management-list'] });
    queryClient.invalidateQueries({ queryKey: ['addon-management-audit'] });
  };

  return {
    // Estatísticas
    stats: statsQuery.data,
    isLoadingStats: statsQuery.isLoading,
    
    // Diagnóstico
    diagnostics: diagnosticsQuery.data,
    isLoadingDiagnostics: diagnosticsQuery.isLoading,
    
    // Lista de add-ons
    addons: addonsQuery.data ?? [],
    isLoadingAddons: addonsQuery.isLoading,
    
    // Logs de auditoria
    auditLogs: auditLogsQuery.data ?? [],
    isLoadingAuditLogs: auditLogsQuery.isLoading,
    
    // Mutations
    createAddon: createAddonMutation.mutateAsync,
    isCreating: createAddonMutation.isPending,
    
    cancelAddon: cancelAddonMutation.mutateAsync,
    isCancelling: cancelAddonMutation.isPending,
    
    // Sync
    syncWithStripe,
    
    // Refetch
    refetchAll,
  };
}

/**
 * Hook para buscar tenants para select
 */
export function useTenantsList() {
  return useQuery({
    queryKey: ['tenants-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenants')
        .select('id, name')
        .order('name');

      if (error) throw error;
      return data || [];
    },
    staleTime: 60000,
  });
}

/**
 * Hook para buscar add-ons disponíveis
 */
export function useAddonsList() {
  return useQuery({
    queryKey: ['addons-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('plan_addons')
        .select('id, name, addon_type, is_active')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data || [];
    },
    staleTime: 60000,
  });
}

/**
 * Hook para buscar providers disponíveis
 */
export function useProvidersList() {
  return useQuery({
    queryKey: ['providers-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('api_providers')
        .select('id, name, slug, is_active')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data || [];
    },
    staleTime: 60000,
  });
}
